export interface AgentVoice {
  voice_id: string;
  name: string;
  provider_name: 'eleven_labs' | 'deepgram' | 'openai';
  provider_props: ElevenLabsProps | DeepgramProps | null;
  sample_audio: string;
  languages: {
    language: string;
    sample_audio?: string;
  }[];
  gender: string;
  description: string;
}

export interface ElevenLabsProps {
  description: string;
  labels: {
    accent: string;
    descriptive?: string;
    age: string;
    gender: string;
    language?: string;
    use_case?: string;
  };
}

export interface DeepgramProps {
  metadata: {
    accent: string;
    age: string;
    color: string;
    image: string;
    sample: string;
    tags: string[];
    use_cases: string[];
  };
}

const DEEPGRAM_VOICES: AgentVoice[] = [
  {
    voice_id: 'aura-2-amalthea-en',
    name: 'Amalthea',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'Filipino',
        age: 'Young Adult',
        color: '#A1D7FD',
        image: 'https://static.deepgram.com/examples/avatars/amalthea.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-amalthea.wav',
        tags: ['feminine', 'engaging', 'natural', 'cheerful'],
        use_cases: ['casual chat']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-amalthea.wav',
    gender: 'female',
    description:
      'A young adult Filipino female voice. Engaging, natural, and cheerful, perfect for casual chat.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-andromeda-en',
    name: 'Andromeda',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#A1D7FD',
        image: 'https://static.deepgram.com/examples/avatars/andromeda.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-andromeda.wav',
        tags: ['feminine', 'casual', 'expressive', 'comfortable'],
        use_cases: ['customer service', 'IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-andromeda.wav',
    gender: 'female',
    description:
      'An adult American female voice. Casual, expressive, and comfortable, suitable for customer service and IVR.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-apollo-en',
    name: 'Apollo',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#A1D7FD',
        image: 'https://static.deepgram.com/examples/avatars/apollo.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-apollo.wav',
        tags: ['masculine', 'confident', 'comfortable', 'casual'],
        use_cases: ['casual chat']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-apollo.wav',
    gender: 'male',
    description:
      'An adult American male voice. Confident, comfortable, and casual, ideal for casual chat.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-arcas-en',
    name: 'Arcas',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#EE028C',
        image: 'https://static.deepgram.com/examples/avatars/arcas.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-arcas.wav',
        tags: ['masculine', 'natural', 'smooth', 'clear', 'comfortable'],
        use_cases: ['customer service', 'casual chat']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-arcas.wav',
    gender: 'male',
    description:
      'An adult American male voice. Natural, smooth, clear, and comfortable, suitable for customer service and casual chat.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-aries-en',
    name: 'Aries',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#EE028C',
        image: 'https://static.deepgram.com/examples/avatars/aries.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-aries.wav',
        tags: ['masculine', 'warm', 'energetic', 'caring'],
        use_cases: ['casual chat']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-aries.wav',
    gender: 'male',
    description:
      'An adult American male voice. Warm, energetic, and caring, perfect for casual chat.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-asteria-en',
    name: 'Asteria',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#7800ED',
        image: 'https://static.deepgram.com/examples/avatars/asteria.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-asteria.wav',
        tags: ['feminine', 'clear', 'confident', 'knowledgeable', 'energetic'],
        use_cases: ['advertising']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-asteria.wav',
    gender: 'female',
    description:
      'An adult American female voice. Clear, confident, knowledgeable, and energetic, ideal for advertising.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-athena-en',
    name: 'Athena',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Mature',
        color: '#F04438',
        image: 'https://static.deepgram.com/examples/avatars/athena.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-athena.wav',
        tags: ['feminine', 'calm', 'smooth', 'professional'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-athena.wav',
    gender: 'female',
    description:
      'A mature American female voice. Calm, smooth, and professional, excellent for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-atlas-en',
    name: 'Atlas',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Mature',
        color: '#7800ED',
        image: 'https://static.deepgram.com/examples/avatars/atlas.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-atlas.wav',
        tags: [
          'masculine',
          'enthusiastic',
          'confident',
          'approachable',
          'friendly'
        ],
        use_cases: ['advertising']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-atlas.wav',
    gender: 'male',
    description:
      'A mature American male voice. Enthusiastic, confident, approachable, and friendly, great for advertising.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-aurora-en',
    name: 'Aurora',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#949498',
        image: 'https://static.deepgram.com/examples/avatars/aurora.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-aurora.wav',
        tags: ['feminine', 'cheerful', 'expressive', 'energetic'],
        use_cases: ['interview']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-aurora.wav',
    gender: 'female',
    description:
      'An adult American female voice. Cheerful, expressive, and energetic, suitable for interviews.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-callista-en',
    name: 'Callista',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#027A48',
        image: 'https://static.deepgram.com/examples/avatars/callista.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-callista.wav',
        tags: ['feminine', 'clear', 'energetic', 'professional', 'smooth'],
        use_cases: ['IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-callista.wav',
    gender: 'female',
    description:
      'An adult American female voice. Clear, energetic, professional, and smooth, perfect for IVR systems.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-cora-en',
    name: 'Cora',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FFADD8',
        image: 'https://static.deepgram.com/examples/avatars/cora.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-cora.wav',
        tags: ['feminine', 'smooth', 'melodic', 'caring'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-cora.wav',
    gender: 'female',
    description:
      'An adult American female voice. Smooth, melodic, and caring, ideal for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-cordelia-en',
    name: 'Cordelia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Young Adult',
        color: '#EE028C',
        image: 'https://static.deepgram.com/examples/avatars/cordelia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-cordelia.wav',
        tags: ['feminine', 'approachable', 'warm', 'polite'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-cordelia.wav',
    gender: 'female',
    description:
      'A young adult American female voice. Approachable, warm, and polite, suitable for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-delia-en',
    name: 'Delia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Young Adult',
        color: '#FFADD8',
        image: 'https://static.deepgram.com/examples/avatars/delia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-delia.wav',
        tags: ['feminine', 'casual', 'friendly', 'cheerful', 'breathy'],
        use_cases: ['interview']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-delia.wav',
    gender: 'female',
    description:
      'A young adult American female voice. Casual, friendly, cheerful, and breathy, great for interviews.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-draco-en',
    name: 'Draco',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'British',
        age: 'Adult',
        color: '#EE028C',
        image: 'https://static.deepgram.com/examples/avatars/draco.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-draco.wav',
        tags: ['masculine', 'warm', 'approachable', 'trustworthy', 'baritone'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-draco.wav',
    gender: 'male',
    description:
      'An adult British male voice. Warm, approachable, trustworthy, with a baritone quality, perfect for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-electra-en',
    name: 'Electra',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FEC84B',
        image: 'https://static.deepgram.com/examples/avatars/electra.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-electra.wav',
        tags: ['feminine', 'professional', 'engaging', 'knowledgeable'],
        use_cases: ['IVR', 'advertising', 'customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-electra.wav',
    gender: 'female',
    description:
      'An adult American female voice. Professional, engaging, and knowledgeable, suitable for IVR, advertising, and customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-harmonia-en',
    name: 'Harmonia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FFE699',
        image: 'https://static.deepgram.com/examples/avatars/harmonia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-harmonia.wav',
        tags: ['feminine', 'empathetic', 'clear', 'calm', 'confident'],
        use_cases: ['customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-harmonia.wav',
    gender: 'female',
    description:
      'An adult American female voice. Empathetic, clear, calm, and confident, ideal for customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-helena-en',
    name: 'Helena',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#7800ED',
        image: 'https://static.deepgram.com/examples/avatars/helena.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-helena.wav',
        tags: [
          'feminine',
          'caring',
          'natural',
          'positive',
          'friendly',
          'raspy'
        ],
        use_cases: ['IVR', 'casual chat']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-helena.wav',
    gender: 'female',
    description:
      'An adult American female voice. Caring, natural, positive, friendly, and raspy, suitable for IVR and casual chat.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-hera-en',
    name: 'Hera',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#949498',
        image: 'https://static.deepgram.com/examples/avatars/hera.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-hera.wav',
        tags: ['feminine', 'smooth', 'warm', 'professional'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-hera.wav',
    gender: 'female',
    description:
      'An adult American female voice. Smooth, warm, and professional, excellent for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-hermes-en',
    name: 'Hermes',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#C58DFF',
        image: 'https://static.deepgram.com/examples/avatars/hermes.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-thalia.wav',
        tags: ['masculine', 'expressive', 'engaging', 'professional'],
        use_cases: ['informative']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-thalia.wav',
    gender: 'male',
    description:
      'An adult American male voice. Expressive, engaging, and professional, great for informative content.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-hyperion-en',
    name: 'Hyperion',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'Australian',
        age: 'Adult',
        color: '#79AFFA',
        image: 'https://static.deepgram.com/examples/avatars/hyperion.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-hyperion.wav',
        tags: ['masculine', 'caring', 'warm', 'empathetic'],
        use_cases: ['interview']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-hyperion.wav',
    gender: 'male',
    description:
      'An adult Australian male voice. Caring, warm, and empathetic, suitable for interviews.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-iris-en',
    name: 'Iris',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Young Adult',
        color: '#949498',
        image: 'https://static.deepgram.com/examples/avatars/iris.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-iris.wav',
        tags: ['feminine', 'cheerful', 'positive', 'approachable'],
        use_cases: ['IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-iris.wav',
    gender: 'female',
    description:
      'A young adult American female voice. Cheerful, positive, and approachable, perfect for IVR systems.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-janus-en',
    name: 'Janus',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#075433',
        image: 'https://static.deepgram.com/examples/avatars/janus.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-janus.wav',
        tags: ['feminine', 'southern', 'smooth', 'trustworthy'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-janus.wav',
    gender: 'female',
    description:
      'An adult American female voice with a southern accent. Smooth and trustworthy, ideal for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-juno-en',
    name: 'Juno',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#C58DFF',
        image: 'https://static.deepgram.com/examples/avatars/juno.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-juno.wav',
        tags: ['feminine', 'natural', 'engaging', 'melodic', 'breathy'],
        use_cases: ['interview']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-juno.wav',
    gender: 'female',
    description:
      'An adult American female voice. Natural, engaging, melodic, and breathy, suitable for interviews.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-jupiter-en',
    name: 'Jupiter',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FEC84B',
        image: 'https://static.deepgram.com/examples/avatars/jupiter.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-jupiter.wav',
        tags: ['masculine', 'expressive', 'knowledgeable', 'baritone'],
        use_cases: ['informative']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-jupiter.wav',
    gender: 'male',
    description:
      'An adult American male voice. Expressive, knowledgeable, with a baritone quality, great for informative content.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-luna-en',
    name: 'Luna',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Young Adult',
        color: '#949498',
        image: 'https://static.deepgram.com/examples/avatars/luna.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-luna.wav',
        tags: ['feminine', 'friendly', 'natural', 'engaging'],
        use_cases: ['IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-luna.wav',
    gender: 'female',
    description:
      'A young adult American female voice. Friendly, natural, and engaging, perfect for IVR systems.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-mars-en',
    name: 'Mars',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FFADD8',
        image: 'https://static.deepgram.com/examples/avatars/mars.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-mars.wav',
        tags: ['masculine', 'smooth', 'patient', 'trustworthy', 'baritone'],
        use_cases: ['customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-mars.wav',
    gender: 'male',
    description:
      'An adult American male voice. Smooth, patient, trustworthy, with a baritone quality, ideal for customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-minerva-en',
    name: 'Minerva',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#A1D7FD',
        image: 'https://static.deepgram.com/examples/avatars/minerva.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-minerva.wav',
        tags: ['feminine', 'positive', 'friendly', 'natural'],
        use_cases: ['storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-minerva.wav',
    gender: 'female',
    description:
      'An adult American female voice. Positive, friendly, and natural, suitable for storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-neptune-en',
    name: 'Neptune',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#F04438',
        image: 'https://static.deepgram.com/examples/avatars/neptune.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-neptune.wav',
        tags: ['masculine', 'professional', 'patient', 'polite'],
        use_cases: ['customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-neptune.wav',
    gender: 'male',
    description:
      'An adult American male voice. Professional, patient, and polite, great for customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-odysseus-en',
    name: 'Odysseus',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#F04438',
        image: 'https://static.deepgram.com/examples/avatars/odysseus.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-odysseus.wav',
        tags: ['masculine', 'calm', 'smooth', 'comfortable', 'professional'],
        use_cases: ['advertising']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-odysseus.wav',
    gender: 'male',
    description:
      'An adult American male voice. Calm, smooth, comfortable, and professional, perfect for advertising.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-ophelia-en',
    name: 'Ophelia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FFADD8',
        image: 'https://static.deepgram.com/examples/avatars/ophelia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-ophelia.wav',
        tags: ['feminine', 'expressive', 'enthusiastic', 'cheerful'],
        use_cases: ['interview']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-ophelia.wav',
    gender: 'female',
    description:
      'An adult American female voice. Expressive, enthusiastic, and cheerful, ideal for interviews.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-orion-en',
    name: 'Orion',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#A1D7FD',
        image: 'https://static.deepgram.com/examples/avatars/orion.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-orion.wav',
        tags: ['masculine', 'approachable', 'comfortable', 'calm', 'polite'],
        use_cases: ['informative']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-orion.wav',
    gender: 'male',
    description:
      'An adult American male voice. Approachable, comfortable, calm, and polite, suitable for informative content.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-orpheus-en',
    name: 'Orpheus',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#A1F9D4',
        image: 'https://static.deepgram.com/examples/avatars/orpheus.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-orpheus.wav',
        tags: [
          'masculine',
          'professional',
          'clear',
          'confident',
          'trustworthy'
        ],
        use_cases: ['customer service', 'storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-orpheus.wav',
    gender: 'male',
    description:
      'An adult American male voice. Professional, clear, confident, and trustworthy, great for customer service and storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-pandora-en',
    name: 'Pandora',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'British',
        age: 'Adult',
        color: '#FEDF89',
        image: 'https://static.deepgram.com/examples/avatars/pandora.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-pandora.wav',
        tags: ['feminine', 'smooth', 'calm', 'melodic', 'breathy'],
        use_cases: ['IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-pandora.wav',
    gender: 'female',
    description:
      'An adult British female voice. Smooth, calm, melodic, and breathy, suitable for IVR.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-phoebe-en',
    name: 'Phoebe',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#EE028C',
        image: 'https://static.deepgram.com/examples/avatars/phoebe.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-phoebe.wav',
        tags: ['feminine', 'energetic', 'warm', 'casual'],
        use_cases: ['customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-phoebe.wav',
    gender: 'female',
    description:
      'An adult American female voice. Energetic, warm, and casual, suitable for customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-pluto-en',
    name: 'Pluto',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#027A48',
        image: 'https://static.deepgram.com/examples/avatars/pluto.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-pluto.wav',
        tags: ['masculine', 'smooth', 'calm', 'empathetic', 'baritone'],
        use_cases: ['interview', 'storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-pluto.wav',
    gender: 'male',
    description:
      'An adult American male voice. Smooth, calm, empathetic, with a baritone quality, suitable for interviews and storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-saturn-en',
    name: 'Saturn',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FEC84B',
        image: 'https://static.deepgram.com/examples/avatars/saturn.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-saturn.wav',
        tags: ['masculine', 'knowledgeable', 'confident', 'baritone'],
        use_cases: ['customer service']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-saturn.wav',
    gender: 'male',
    description:
      'An adult American male voice. Knowledgeable, confident, with a baritone quality, suitable for customer service.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-selene-en',
    name: 'Selene',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#F04438',
        image: 'https://static.deepgram.com/examples/avatars/selene.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-selene.wav',
        tags: ['feminine', 'expressive', 'engaging', 'energetic'],
        use_cases: ['informative']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-selene.wav',
    gender: 'female',
    description:
      'An adult American female voice. Expressive, engaging, and energetic, suitable for informative content.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-thalia-en',
    name: 'Thalia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FFE699',
        image: 'https://static.deepgram.com/examples/avatars/thalia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-thalia.wav',
        tags: ['feminine', 'clear', 'confident', 'energetic', 'enthusiastic'],
        use_cases: ['casual chat', 'customer service', 'IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-thalia.wav',
    gender: 'female',
    description:
      'An adult American female voice. Clear, confident, energetic, and enthusiastic, suitable for casual chat, customer service, and IVR.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-theia-en',
    name: 'Theia',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#FEC84B',
        image: 'https://static.deepgram.com/examples/avatars/theia.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-theia.wav',
        tags: ['feminine', 'expressive', 'polite', 'sincere'],
        use_cases: ['informative']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-theia.wav',
    gender: 'female',
    description:
      'An adult American female voice. Expressive, polite, and sincere, suitable for informative content.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-vesta-en',
    name: 'Vesta',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#149AFB',
        image: 'https://static.deepgram.com/examples/avatars/vesta.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-vesta.wav',
        tags: ['feminine', 'natural', 'expressive', 'patient', 'empathetic'],
        use_cases: ['customer service', 'interview', 'storytelling']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-vesta.wav',
    gender: 'female',
    description:
      'An adult American female voice. Natural, expressive, patient, and empathetic, suitable for customer service, interviews, and storytelling.',
    languages: [
      {
        language: 'en'
      }
    ]
  },
  {
    voice_id: 'aura-2-zeus-en',
    name: 'Zeus',
    provider_name: 'deepgram',
    provider_props: {
      metadata: {
        accent: 'American',
        age: 'Adult',
        color: '#C58DFF',
        image: 'https://static.deepgram.com/examples/avatars/zeus.jpg',
        sample: 'https://static.deepgram.com/examples/Aura-2-zeus.wav',
        tags: ['masculine', 'deep', 'trustworthy', 'smooth'],
        use_cases: ['IVR']
      }
    },
    sample_audio: 'https://static.deepgram.com/examples/Aura-2-zeus.wav',
    gender: 'male',
    description:
      'An adult American male voice. Deep, trustworthy, and smooth, suitable for IVR.',
    languages: [
      {
        language: 'en'
      }
    ]
  }
];

export function voiceLangCodeToLabel(langCode: string) {
  return {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ru: 'Russian',
    ja: 'Japanese',
    ko: 'Korean',
    zh: 'Chinese',
    ar: 'Arabic',
    hi: 'Hindi',
    bn: 'Bengali',
    nl: 'Dutch',
    pl: 'Polish',
    ro: 'Romanian',
    sv: 'Swedish',
    tr: 'Turkish',
    uk: 'Ukrainian',
    vi: 'Vietnamese',
    id: 'Indonesian',
    ms: 'Malay',
    th: 'Thai',
    la: 'Lao',
    km: 'Khmer',
    lo: 'Lao',
    my: 'Myanmar',
    fil: 'Filipino',
    ph: 'Filipino',
    ta: 'Tamil',
    te: 'Telugu',
    mr: 'Marathi',
    gu: 'Gujarati',
    pa: 'Punjabi',
    or: 'Oriya'
  }[langCode];
}

export const OPENAI_VOICES = [
  { value: 'alloy', name: 'Alloy', extension: 'wav', gender: 'female' },
  { value: 'ash', name: 'Ash', extension: 'flac', gender: 'male' },
  { value: 'ballad', name: 'Ballad', extension: 'flac', gender: 'male' },
  { value: 'coral', name: 'Coral', extension: 'flac', gender: 'female' },
  { value: 'echo', name: 'Echo', extension: 'wav', gender: 'male' },
  { value: 'sage', name: 'Sage', extension: 'flac', gender: 'female' },
  { value: 'shimmer', name: 'Shimmer', extension: 'wav', gender: 'female' },
  { value: 'verse', name: 'Verse', extension: 'flac', gender: 'male' }
];

const OPENAI_LANGUAGES = [
  { language: 'af' },
  { language: 'ar' },
  { language: 'hy' },
  { language: 'az' },
  { language: 'be' },
  { language: 'bs' },
  { language: 'bg' },
  { language: 'ca' },
  { language: 'zh' },
  { language: 'hr' },
  { language: 'cs' },
  { language: 'da' },
  { language: 'nl' },
  { language: 'en' },
  { language: 'et' },
  { language: 'fi' },
  { language: 'fr' },
  { language: 'gl' },
  { language: 'de' },
  { language: 'el' },
  { language: 'he' },
  { language: 'hi' },
  { language: 'hu' },
  { language: 'is' },
  { language: 'id' },
  { language: 'it' },
  { language: 'ja' },
  { language: 'kn' },
  { language: 'kk' },
  { language: 'ko' },
  { language: 'lv' },
  { language: 'lt' },
  { language: 'mk' },
  { language: 'ms' },
  { language: 'mr' },
  { language: 'mi' },
  { language: 'ne' },
  { language: 'no' },
  { language: 'fa' },
  { language: 'pl' },
  { language: 'pt' },
  { language: 'ro' },
  { language: 'ru' },
  { language: 'sr' },
  { language: 'sk' },
  { language: 'sl' },
  { language: 'es' },
  { language: 'sw' },
  { language: 'sv' },
  { language: 'tl' },
  { language: 'ta' },
  { language: 'th' },
  { language: 'tr' },
  { language: 'uk' },
  { language: 'ur' },
  { language: 'vi' },
  { language: 'cy' }
];

export function isOpenAIVoice(voice: string) {
  return OPENAI_VOICES.some(v => v.value === voice) || voice === 'engage';
}

export function getOpenAIVoiceModel(voice: string, fallback = 'shimmer') {
  if (voice === 'engage') {
    return 'engage';
  }
  return OPENAI_VOICES.find(v => v.value === voice)?.value || fallback;
}

const OPENAI_AGENT_VOICES: AgentVoice[] = OPENAI_VOICES.map(voice => ({
  voice_id: voice.value,
  name: voice.name,
  provider_name: 'openai',
  provider_props: null,
  sample_audio: `https://cdn.openai.com/API/voice-previews/${voice.value}.flac`,
  languages: OPENAI_LANGUAGES,
  gender: voice.gender,
  description: 'Multi-lingual voice (Experimental)'
}));

export const AGENT_VOICES: AgentVoice[] = [
  ...DEEPGRAM_VOICES,
  ...OPENAI_AGENT_VOICES
].map(voice => {
  const languages = Array.from(
    new Set(voice.languages.map(language => language.language))
  );
  return {
    ...voice,
    languages: languages
      .map(lang => ({
        language: voiceLangCodeToLabel(lang) as string
      }))
      .filter(lang => lang.language)
  };
});

export function findVoice(voiceId: string) {
  return AGENT_VOICES.find(voice => voice.voice_id === voiceId);
}
